type AnalyticsApp = 'customer' | 'admin';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: any[]) => void;
    clarity?: (...args: any[]) => void;
  }
}

// Default Google Tag ID if not overridden in .env
const DEFAULT_MEASUREMENT_ID = 'AW-18496898726';

// IMPORTANT: avoid `process` at runtime in the browser. Webpack inlines `process.env.REACT_APP_*` when present.
const PROVIDER = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_ANALYTICS_PROVIDER ? process.env.REACT_APP_ANALYTICS_PROVIDER : 'ga4').trim().toLowerCase(); // 'ga4' | 'clarity' | ''
const GA4_MEASUREMENT_ID = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_GA4_MEASUREMENT_ID ? process.env.REACT_APP_GA4_MEASUREMENT_ID : '').trim() || DEFAULT_MEASUREMENT_ID;
const CLARITY_PROJECT_ID = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_CLARITY_PROJECT_ID ? process.env.REACT_APP_CLARITY_PROJECT_ID : '').trim();
const ENV_ID = ((typeof process !== 'undefined' && process.env && process.env.REACT_APP_STAGE) ? process.env.REACT_APP_STAGE : '').trim() || ((typeof process !== 'undefined' && process.env && process.env.NODE_ENV) ? process.env.NODE_ENV : '').trim() || 'unknown';

let initialized = false;
let consentGiven = false;
let pendingPageViewApp: AnalyticsApp | null = null;
let lastTrackedPath = '';
let pageViewTimeout: ReturnType<typeof setTimeout> | null = null;
let autoClickCleanup: (() => void) | null = null;
let lastClickTimestamp = 0;

/**
 * Checks if window.gtag is available (either from HTML script tag or dynamic shim).
 */
function isGtagReady(): boolean {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
}

/**
 * Set analytics consent status (called when user accepts cookies).
 */
export function setAnalyticsConsent(consent: boolean): void {
  consentGiven = consent;
  if (!consent) {
    initialized = false;
    pendingPageViewApp = null;
    if (isGtagReady()) {
      try {
        window.gtag?.('consent', 'update', {
          analytics_storage: 'denied',
          ad_storage: 'denied',
        });
      } catch {
        // ignore
      }
    }
  } else {
    if (isGtagReady()) {
      try {
        window.gtag?.('consent', 'update', {
          analytics_storage: 'granted',
          ad_storage: 'granted',
        });
      } catch {
        // ignore
      }
    }
    // Flush pending landing page view immediately
    if (pendingPageViewApp) {
      const app = pendingPageViewApp;
      pendingPageViewApp = null;
      trackPageView(app);
    } else {
      trackPageView('customer');
    }
  }
}

/**
 * Update consent status from localStorage (called on mount and before tracking).
 */
export function updateAnalyticsConsentFromStorage(): void {
  try {
    const stored = localStorage.getItem('vastra_cookie_consent');
    consentGiven = stored === 'accepted';
  } catch {
    consentGiven = false;
  }
}

function ensureGtagLoaded(): void {
  if (typeof window === 'undefined') return;
  if (!consentGiven) return;

  if (isGtagReady() && initialized) return;

  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtagShim(...args: any[]) {
      (window.dataLayer as unknown[]).push(args);
    };

  const idToLoad = GA4_MEASUREMENT_ID;
  if (idToLoad) {
    const existingScript = document.querySelector(`script[src*="${encodeURIComponent(idToLoad)}"]`);
    if (!existingScript) {
      const s = document.createElement('script');
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(idToLoad)}`;
      document.head.appendChild(s);
    }

    if (!initialized) {
      window.gtag('js', new Date());
      window.gtag('config', idToLoad, { send_page_view: false });
    }
  }

  initialized = true;
}

function ensureClarityLoaded(): void {
  if (!CLARITY_PROJECT_ID) return;
  if (!consentGiven) return;
  if (initialized) return;
  initialized = true;

  (function (c: any, l: any, a: any, r: any, i: any, t?: any, y?: any) {
    c[a] =
      c[a] ||
      function () {
        (c[a].q = c[a].q || []).push(arguments);
      };
    t = l.createElement(r);
    t.async = 1;
    t.src = `https://www.clarity.ms/tag/${encodeURIComponent(i)}`;
    y = l.getElementsByTagName(r)[0];
    y.parentNode.insertBefore(t, y);
  })(window, document, 'clarity', 'script', CLARITY_PROJECT_ID);
}

/**
 * Tracks a virtual page view for Single Page Application route transitions.
 * Includes page title, location, path, and deduplication with settled title delay.
 */
export function trackPageView(app: AnalyticsApp, params?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;

  updateAnalyticsConsentFromStorage();

  if (!consentGiven) {
    pendingPageViewApp = app;
    return;
  }

  if (PROVIDER === 'clarity') {
    if (!CLARITY_PROJECT_ID) return;
    ensureClarityLoaded();
    window.clarity?.('set', 'portal', app);
    window.clarity?.('set', 'env', ENV_ID);
    window.clarity?.('event', 'page_view');
    return;
  }

  ensureGtagLoaded();
  if (!isGtagReady() && !GA4_MEASUREMENT_ID) return;

  if (pageViewTimeout) {
    clearTimeout(pageViewTimeout);
  }

  // Defer slightly (100ms) to allow document.title and SEO tags to be set by the new page component
  pageViewTimeout = setTimeout(() => {
    const page_location = window.location.href;
    const page_path = window.location.pathname + window.location.search;
    const page_title = document.title || window.location.pathname;

    if (lastTrackedPath === page_path) {
      return;
    }
    lastTrackedPath = page_path;

    window.gtag?.('event', 'page_view', {
      page_location,
      page_path,
      page_title,
      portal: app,
      env: ENV_ID,
      ...(params ?? {}),
    });
  }, 100);
}

/**
 * Tracks a custom analytics event.
 */
export function trackEvent(app: AnalyticsApp, name: string, params?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;

  updateAnalyticsConsentFromStorage();
  if (!consentGiven) return;

  if (PROVIDER === 'clarity') {
    if (!CLARITY_PROJECT_ID) return;
    ensureClarityLoaded();
    window.clarity?.('set', 'portal', app);
    window.clarity?.('set', 'env', ENV_ID);
    window.clarity?.('event', name);
    return;
  }

  ensureGtagLoaded();
  if (!isGtagReady() && !GA4_MEASUREMENT_ID) return;

  window.gtag?.('event', name, {
    ...(params ?? {}),
    portal: app,
    env: ENV_ID,
  });
}

/**
 * Explicit helper to track a named click event.
 */
export function trackClick(app: AnalyticsApp, clickName: string, params?: Record<string, unknown>): void {
  trackEvent(app, 'click', {
    click_name: clickName,
    page_path: typeof window !== 'undefined' ? window.location.pathname : '',
    ...(params ?? {}),
  });
}

/**
 * Installs an automatic click listener on document to track interactive clicks:
 * - Button clicks (<button>, [role="button"])
 * - Outbound links (external URLs)
 * - WhatsApp links (wa.me, whatsapp.com)
 * - Elements with data-analytics-click or data-track-click
 */
export function initAutoClickTracking(app: AnalyticsApp): () => void {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return () => {};
  }
  if (autoClickCleanup) {
    return autoClickCleanup;
  }

  const handleClick = (e: MouseEvent) => {
    try {
      const now = Date.now();
      // Debounce rapid click spam (< 200ms)
      if (now - lastClickTimestamp < 200) {
        return;
      }

      const target = e.target as HTMLElement | null;
      if (!target) return;

      const interactive = target.closest<HTMLElement>(
        'a, button, [role="button"], input[type="button"], input[type="submit"], [data-analytics-click], [data-track-click]'
      );
      if (!interactive) return;

      // Ignore regular text/number inputs
      if (
        interactive.tagName === 'INPUT' &&
        !['button', 'submit', 'reset'].includes((interactive as HTMLInputElement).type)
      ) {
        return;
      }

      // Ignore elements explicitly marked to be ignored or inside sensitive forms
      if (
        interactive.closest('[data-analytics-ignore]') ||
        interactive.getAttribute('data-analytics-ignore') === 'true'
      ) {
        return;
      }

      lastClickTimestamp = now;

      const tagName = interactive.tagName.toLowerCase();
      const role = interactive.getAttribute('role') || '';
      const customEvent =
        interactive.getAttribute('data-analytics-click') ||
        interactive.getAttribute('data-track-click');

      const rawText =
        interactive.getAttribute('aria-label') ||
        interactive.getAttribute('title') ||
        interactive.getAttribute('name') ||
        interactive.innerText ||
        interactive.textContent ||
        '';
      const clickText = rawText.trim().replace(/\s+/g, ' ').slice(0, 60);

      const id = interactive.id ? interactive.id.slice(0, 50) : undefined;
      const href = interactive.getAttribute('href') || undefined;

      // 1. WhatsApp click (vital for storefront conversions)
      if (href && (href.includes('wa.me') || href.includes('whatsapp.com'))) {
        trackEvent(app, 'whatsapp_click', {
          link_url: href,
          link_text: clickText || 'WhatsApp',
          click_id: id,
          page_path: window.location.pathname,
        });
        return;
      }

      // 2. Outbound link click
      let isOutbound = false;
      if (tagName === 'a' && href && !href.startsWith('#') && !href.startsWith('/') && !href.startsWith('javascript:')) {
        try {
          const url = new URL(href, window.location.href);
          isOutbound = url.hostname !== window.location.hostname;
        } catch {
          // invalid url
        }
      }

      if (isOutbound) {
        trackEvent(app, 'outbound_click', {
          link_url: href,
          link_text: clickText,
          click_id: id,
          page_path: window.location.pathname,
        });
        return;
      }

      // 3. Custom-tagged event
      if (customEvent) {
        const category = interactive.getAttribute('data-analytics-category') || 'interaction';
        const label = interactive.getAttribute('data-analytics-label') || clickText;
        trackEvent(app, customEvent, {
          category,
          label,
          click_text: clickText,
          click_id: id,
          page_path: window.location.pathname,
        });
        return;
      }

      // 4. Standard buttons and action links
      if (tagName === 'button' || role === 'button' || tagName === 'a') {
        if (!clickText && !id) return;

        trackEvent(app, 'click', {
          click_text: clickText,
          click_type: role || tagName,
          click_id: id,
          link_url: href,
          page_path: window.location.pathname,
        });
      }
    } catch {
      // safe fallback — never break UI on analytics
    }
  };

  document.addEventListener('click', handleClick, { passive: true, capture: true });

  autoClickCleanup = () => {
    document.removeEventListener('click', handleClick, { capture: true });
    autoClickCleanup = null;
  };

  return autoClickCleanup;
}
